'use client';

/**
 * ProductSearchResultsCard — the canvas home for product_search results.
 *
 * Store-driven (mode3 pattern): reads {query, filters} from the
 * product-search store, fetches the `product_search` strategy, and paints
 * the SAME `ProductTile` grid that browsing a vendor paints. The rail
 * search card owns the input + facets; this card owns the results. Both
 * fetch the same params, so React Query dedups the round-trip.
 *
 * Thin painter: the tile is borrowed verbatim from browse; click fires the
 * same selectNode('product') the catalogue does, so a search-result tile
 * behaves identically to a browsed one (rail detail, add-to-RFQ, PDP).
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_search_results"
 * @see components/canvas/cards/vendors/product-tile.tsx — the shared tile
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import {
  useProductSearchStore,
  type ProductSearchFilters,
} from '@/lib/stores/product-search-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useTabStore } from '@/lib/stores/tab-store';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { CardPlacementProvider, useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { ProductSummary } from '@/lib/api/types';
import { ProductTile } from '../vendors/product-tile';
import { CardGrid } from '../card-grid';
import { SwimlaneCard } from '../swimlane-card';
import { type ResultSummary } from './result-summary-card';

interface ProductHit {
  semantic_id: string;
  name: string | null;
  price: number | null;
  category: string | null;
  image_url: string | null;
  vendor_name: string | null;
  vendor_sid: string | null;
  color: string | null;
  material: string | null;
  score?: number;
  /** No list price: the vendor quotes it ($0 / $1 placeholder in the catalogue). */
  price_on_request?: boolean;
  // The caller's ★ favourite state (SAVED edge) — drives the ♥ fill.
  saved?: boolean;
}

/** A vendor section = the SAME product hits, bucketed by vendor (BFF marshaled).
 *  Not a new card — the items are painted with the shared ProductTile. */
interface VendorShelf {
  vendor_sid: string;
  vendor_name: string;
  count: number;
  monogram: { initials: string; bg?: string; fg?: string };
  items: ProductHit[];
}

interface Suggestion {
  type: string;
  mode: string;        // BFF-declared: the mode that hosts this type (where Switch navigates)
  name: string;
  semantic_id: string;
  match: string;
}

interface ProductSearchResponse {
  products: ProductHit[];
  total: number;
  returned: number;
  capped: boolean;
  facets: unknown[];
  // Cross-index peek: products floored + a lexical hit elsewhere → redirect.
  no_match?: boolean;
  suggestion?: Suggestion | null;
  // Grouped view (group_by="vendor") — the shelves, marshaled by the BFF.
  grouped_by?: string;
  summary?: ResultSummary;
  shelves?: VendorShelf[];
  other_vendors?: { vendor_count: number; count: number } | null;
}

/** Search hit → the browse tile's ProductSummary shape (price→list_price;
 *  search doesn't return qty). */
function toSummary(hit: ProductHit): ProductSummary {
  return {
    semantic_id: hit.semantic_id,
    name: hit.name ?? 'Unnamed',
    list_price: hit.price,
    qty_available: null,
    image_url: hit.image_url,
    saved: hit.saved ?? false,
  };
}

/**
 * Search-state source, scoped to the surface.
 *
 * Ask overlay (`initialQuery` present): the card owns its query in LOCAL
 * state, seeded once from the ask. It NEVER writes the global browse store —
 * otherwise a conversational search leaks into mode4's explorer lens (a
 * phantom filter badge on whatever vendor the user is actually browsing).
 * Browse (no `initialQuery`): the shared store, so the sibling facets card
 * and the Browse/Filter lens toggle stay in sync. Only the five touchpoints
 * this card uses are unified; chip-editing cards keep reading the store.
 */
function useSearchState(initialQuery?: string) {
  const isolated = initialQuery != null;

  const gQuery = useProductSearchStore((s) => s.query);
  const gFilters = useProductSearchStore((s) => s.filters);

  // Isolated (Ask overlay / any injected caller): the query is a LIVE prop —
  // the search terms are parameterized IN, so a seed that resolves AFTER the
  // card first mounts (compose lands post-render) still drives the fetch.
  // (Was `useState(() => initialQuery ?? '')`, frozen at first render, so a
  // late-arriving seed never reached the query → the card stuck on its empty
  // state.) Filter state stays local so an overlay search never writes the
  // shared browse store (the phantom-filter guard).
  const [lFilters] = useState<ProductSearchFilters>({});

  return isolated
    ? { query: initialQuery ?? '', filters: lFilters }
    : { query: gQuery, filters: gFilters };
}

export function ProductSearchResultsCard({
  // Per-placement prop (PlacementSpec.props): "none" = flat wall (mode11),
  // "vendor" = shelves (mode4). This prop only says what to ASK; the card
  // renders off the RESPONSE's `grouped_by` (below).
  group_by = 'none',
  // BFF-shipped seed (Ask Klyde's renderer slot): the user's own ask,
  // carried as `renderer_query` on the knowledge facet. Its presence puts
  // the card in isolated (overlay) mode — see useSearchState.
  initialQuery,
}: {
  group_by?: string;
  initialQuery?: string;
}) {
  const { query, filters } = useSearchState(initialQuery);
  const strategyName = useCapabilityStore((s) => s.strategyName('product_intelligence', 'search'));
  // Per-lane expand state (ephemeral UI): which vendor lanes are opened to
  // their full wall. Collapsed by default → one row each.
  const [expandedLanes, setExpandedLanes] = useState<Set<string>>(new Set());
  const toggleLane = (sid: string) =>
    setExpandedLanes((s) => {
      const next = new Set(s);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
  // inspectEntity (not selectNode): shows the product's brief in the
  // rail WITHOUT changing the canvas selection ('search'), so the results
  // grid stays put. selectNode would be masked by explorerSelection
  // ('search') AND would clear inspection — see viewport-selection.ts.
  const inspectEntity = useGraphSelectionStore((s) => s.inspectEntity);
  // The inspected product drives the Brief; read it back so the matching tile
  // paints a selected ring (the canvas shows what the rail is describing).
  const inspectedEntity = useGraphSelectionStore((s) => s.inspectedEntity);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setActiveExplorerView = useLayoutStore((s) => s.setActiveExplorerView);
  const openTab = useTabStore((s) => s.openTab);

  const labelEmpty = useProductLabel('search_results_empty');
  const labelSwitch = useProductLabel('search_switch');
  // Templates read at top (hook rule); {var} interpolated in-render via replace.
  const noProductsTpl = useProductLabel('search_no_products');
  const foundInTpl = useProductLabel('search_found_in');
  // Lane chrome (labels/product.py). useProductLabel echoes the key when a
  // label is missing, so these MUST exist there — no JS-side fallback.
  const showAllLabel = useProductLabel('search_show_all'); // "Show all {n}"
  const priceOnRequestLabel = useProductLabel('search_price_on_request');
  const collapseLabel = useProductLabel('search_collapse');
  const loadMoreLabel = useProductLabel('wall_load_more');  // "Show more"
  const countTpl = useProductLabel('search_count');         // "{shown} of {total}"

  // Flat-wall pagination (group_by="none" only): browse pages are real
  // server pages (graph ORDER BY name + SKIP/LIMIT), accumulated here.
  // Any change to the search key resets to page 0 and drops the pile.
  const [page, setPage] = useState(0);
  const [priorPages, setPriorPages] = useState<ProductHit[]>([]);
  const searchKey = useMemo(
    () => JSON.stringify({ query, filters, group_by }),
    [query, filters, group_by],
  );
  useEffect(() => {
    setPage(0);
    setPriorPages([]);
  }, [searchKey]);

  // One fast query; the placement decides the collapse. group_by="vendor"
  // fetches the marshaled shelves (expanding a lane is render-only);
  // "none" fetches a flat page of the wall.
  const params = useMemo(
    () => ({ query, filters, limit: 150, page, group_by }),
    [query, filters, page, group_by],
  );
  const enabled =
    !!strategyName && (query.trim().length > 0 || Object.keys(filters).length > 0);
  const { data, isLoading, error } = useStrategyData<ProductSearchResponse>(
    strategyName,
    params,
    enabled,
  );
  const res = data?.data;
  // Render off what the BFF ACTUALLY grouped by, never the input prop. Absent
  // (browse / pre-`grouped_by` payload) → flat.
  const grouping = res?.grouped_by ?? 'none';
  // The BFF's own count: "{shown} of {total}" (returned of the full match set).
  const queryKey = useMemo(
    () => ['strategy-data', strategyName, params] as const,
    [strategyName, params],
  );

  useCardExport(
    useMemo(() => {
      const status: 'loading' | 'loaded' | 'error' | 'idle' = !enabled
        ? 'idle'
        : isLoading
          ? 'loading'
          : error
            ? 'error'
            : res
              ? 'loaded'
              : 'idle';
      return {
        label: 'Results',
        content_key: 'product_search_results',
        surface: 'canvas' as const,
        status,
        query_key: enabled ? queryKey : null,
        text: query ? `${query} — ${res?.returned ?? 0} of ${res?.total ?? 0}` : 'Results',
        json: res ? { query, total: res.total, returned: res.returned, capped: res.capped } : null,
      };
    }, [enabled, isLoading, error, res, query, queryKey]),
  );

  if (!enabled) {
    return (
      <Centered>
        <Search className="w-10 h-10 text-muted-foreground opacity-40" />
        <p className={text.muted}>{labelEmpty}</p>
      </Centered>
    );
  }

  // Full-screen spinner only for the FIRST page — while a later page
  // loads, the accumulated wall stays painted (footer shows progress).
  if (isLoading && !res && priorPages.length === 0) {
    return (
      <Centered>
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </Centered>
    );
  }

  if (error) {
    return (
      <Centered>
        <p className="text-destructive">{(error as Error).message}</p>
      </Centered>
    );
  }

  // Cross-index peek redirect — pure dispatch of the BFF's nav intent
  // (mode + type + id + name all come from the suggestion; no decisions here).
  const switchTo = (s: Suggestion) => {
    setActiveExplorerView(s.mode);
    openTab({ id: `${s.type}-${s.semantic_id}`, type: s.type, title: s.name, entityId: s.semantic_id });
    selectNode({ id: s.semantic_id, label: s.name, type: s.type });
  };

  if (res?.no_match && res.suggestion) {
    const s = res.suggestion;
    return (
      <Centered>
        <Search className="w-8 h-8 text-muted-foreground opacity-40" />
        <p className={text.muted}>{noProductsTpl.replace('{query}', query)}</p>
        <button
          onClick={() => switchTo(s)}
          className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm hover:border-primary hover:bg-primary/10 transition-colors"
        >
          {foundInTpl.replace('{type}', s.type.toUpperCase())}{' '}
          <span className="font-medium">{s.name}</span> · {labelSwitch}
        </button>
      </Centered>
    );
  }

  // SID-only handoff: the overlay (mode4_product) fetches FullProductDetail via
  // graph_product_explore keyed on this id — the same path a browsed product
  // takes, so search and browse open the identical PDP (gallery included). No
  // per-feeder properties blob to drift from the fetched detail.
  const onPick = (hit: ProductHit) =>
    inspectEntity({
      id: hit.semantic_id,
      label: hit.name ?? '',
      type: 'product',
    });

  const tile = (hit: ProductHit, slotVendor: string, showVendor = false) => (
    <CardPlacementProvider
      key={hit.semantic_id}
      value={{ surface: 'canvas', slot_id: `product:${slotVendor}:${hit.semantic_id}` }}
    >
      <ProductTile
        product={toSummary(hit)}
        vendorId={hit.vendor_sid ?? ''}
        vendorName={hit.vendor_name ?? ''}
        // Shelves: redundant — the lane header names the vendor.
        // Flat wall: the vendor line rides the tile (settled decision).
        showVendor={showVendor}
        queryKey={queryKey}
        collectionName=""
        onClick={() => onPick(hit)}
        isSelected={inspectedEntity?.id === hit.semantic_id}
        priceText={hit.price_on_request ? priceOnRequestLabel : undefined}
      />
    </CardPlacementProvider>
  );

  // Flat wall (group_by="none") — the same CardGrid + ProductTile
  // primitives the vendor catalog paints with; order from the BFF
  // (relevance for queries, name for browse), no client regrouping.
  // Pages accumulate: "Show more" appends the next server page.
  if (grouping === 'none') {
    const wallItems = [...priorPages, ...(res?.products ?? [])];
    const total = res?.total ?? wallItems.length;
    const hasMore = wallItems.length < total;
    const loadMore = () => {
      if (res?.products) setPriorPages((prev) => [...prev, ...res.products]);
      setPage((p) => p + 1);
    };
    return (
      <div className="h-full overflow-y-auto p-4 space-y-3">
        <CardGrid>
          {wallItems.map((hit) => tile(hit, 'wall', true))}
        </CardGrid>
        <div className="flex items-center justify-center gap-3 pb-2">
          <span className={text.muted}>
            {countTpl.replace('{shown}', String(wallItems.length)).replace('{total}', String(total))}
          </span>
          {hasMore && (
            <button
              onClick={loadMore}
              disabled={isLoading}
              className={cn(
                text.body,
                'rounded-lg border border-border bg-card px-3 py-1.5 hover:border-primary/40 hover:bg-accent/30 transition-colors disabled:opacity-50',
              )}
            >
              {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : loadMoreLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      {/* One reusable lane card per vendor — the SAME product cards as children,
          collapsed to one row, expand reveals that vendor's full wall. */}
      {(res?.shelves ?? []).map((shelf) => (
        <CardPlacementProvider
          key={shelf.vendor_sid}
          value={{ surface: 'canvas', slot_id: `vendor_shelf:${shelf.vendor_sid}` }}
        >
          <SwimlaneCard
            title={shelf.vendor_name}
            count={shelf.count}
            laneId={shelf.vendor_sid}
            contentKey="vendor_shelf"
            expanded={expandedLanes.has(shelf.vendor_sid)}
            onExpandToggle={() => toggleLane(shelf.vendor_sid)}
            expandLabel={showAllLabel}
            collapseLabel={collapseLabel}
          >
            {shelf.items.map((hit) => tile(hit, shelf.vendor_sid))}
          </SwimlaneCard>
        </CardPlacementProvider>
      ))}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-3 text-center">
      {children}
    </div>
  );
}
